import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, X, Check, Loader2 } from 'lucide-react';
import { postSponsorLead } from '../../../../api';

const ORANGE = '#e57605';
const ORANGE_LIGHT = '#f29b2a';

interface Props {
  arena?: any;
  city?: any;
}

function SponsorCTA({ arena, city }: Props) {
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const arenaLabel = arena?.value
    ? `${arena.value}${city?.value ? ' · ' + city.value : ''}`
    : '';

  const close = () => {
    setOpen(false);
    // reseta depois da animação de saída
    setTimeout(() => {
      setStatus('idle');
      setBrand('');
      setPhone('');
    }, 250);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brand.trim() || phone.replace(/\D/g, '').length < 8) return;
    setStatus('loading');
    try {
      await postSponsorLead({
        full_name: brand.trim(),
        phone: phone.trim(),
        arena_name: arenaLabel || (arena?.value ?? ''),
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      {/* Banner/botão no topo da listagem */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6">
        <div
          className="relative overflow-hidden rounded-2xl border flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 p-4 sm:p-5"
          style={{ borderColor: 'rgba(229,118,5,0.35)', background: 'linear-gradient(120deg, rgba(229,118,5,0.14), rgba(255,255,255,0))' }}
        >
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center" style={{ background: 'rgba(229,118,5,0.18)', border: '1px solid rgba(229,118,5,0.35)' }}>
              <Megaphone className="w-5 h-5" style={{ color: ORANGE_LIGHT }} />
            </div>
            <div>
              <p className="text-white font-semibold leading-tight">Sua marca em todos esses vídeos</p>
              <p className="text-zinc-400 text-sm mt-0.5">
                Anuncie {arena?.value ? <span className="text-zinc-300">na {arena.value}</span> : 'nesta arena'} e apareça nos lances que os atletas compartilham.
              </p>
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-black transition-transform hover:scale-[1.02]"
            style={{ background: `linear-gradient(135deg, ${ORANGE_LIGHT}, ${ORANGE})` }}
          >
            <Megaphone className="w-4 h-4" />
            Quero minha marca nos vídeos
          </button>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center px-4"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
            onClick={close}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-3xl border border-zinc-800 bg-zinc-900/95 p-7"
            >
              <button onClick={close} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>

              {status === 'success' ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)' }}>
                    <Check className="w-7 h-7 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Recebemos seu interesse!</h3>
                  <p className="text-zinc-400 text-sm mt-2">Em breve entramos em contato para colocar a sua marca nos vídeos.</p>
                  <button onClick={close} className="mt-6 w-full py-3 rounded-xl font-semibold text-black" style={{ background: `linear-gradient(135deg, ${ORANGE_LIGHT}, ${ORANGE})` }}>
                    Fechar
                  </button>
                </div>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(229,118,5,0.12)', border: '1px solid rgba(229,118,5,0.3)' }}>
                    <Megaphone className="w-6 h-6" style={{ color: ORANGE_LIGHT }} />
                  </div>
                  <h3 className="text-xl font-bold text-white">Quero minha marca nos vídeos</h3>
                  <p className="text-zinc-400 text-sm mt-1.5 mb-5">Deixe seus dados e a gente te explica como sua marca aparece em cada lance.</p>

                  <form onSubmit={submit} className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-zinc-400">Nome da marca</label>
                      <input
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                        autoFocus
                        placeholder="Ex: Padaria do João"
                        className="w-full mt-1.5 h-11 px-3 rounded-xl bg-black/40 border border-zinc-700 text-white text-sm outline-none focus:ring-2"
                        style={{ caretColor: ORANGE_LIGHT }}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-zinc-400">Telefone / WhatsApp</label>
                      <input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/[^\d\s()+-]/g, ''))}
                        inputMode="tel"
                        placeholder="(31) 99999-9999"
                        className="w-full mt-1.5 h-11 px-3 rounded-xl bg-black/40 border border-zinc-700 text-white text-sm outline-none focus:ring-2"
                        style={{ caretColor: ORANGE_LIGHT }}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-zinc-400">Arena que quer patrocinar</label>
                      <input
                        value={arenaLabel}
                        readOnly
                        placeholder="Selecione uma arena na busca"
                        className="w-full mt-1.5 h-11 px-3 rounded-xl bg-zinc-800/60 border border-zinc-700 text-zinc-300 text-sm outline-none cursor-default"
                      />
                    </div>

                    {status === 'error' && (
                      <p className="text-red-400 text-xs">Não foi possível enviar agora. Tente novamente.</p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading' || !brand.trim() || phone.replace(/\D/g, '').length < 8}
                      className="w-full py-3 rounded-xl font-semibold text-black flex items-center justify-center gap-2 transition-opacity disabled:opacity-40"
                      style={{ background: `linear-gradient(135deg, ${ORANGE_LIGHT}, ${ORANGE})` }}
                    >
                      {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
                      {status === 'loading' ? 'Enviando…' : 'Enviar interesse'}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default SponsorCTA;
